import type { MapProvider } from '@/api'

export interface GeoPoint {
  latitude: number
  longitude: number
}

function point(latitude: number, longitude: number): GeoPoint | null {
  const valid = Number.isFinite(latitude) && Number.isFinite(longitude)
  return valid && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
    ? { latitude, longitude }
    : null
}

function pair(text: string): GeoPoint | null {
  const match = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(text)
  return match ? point(Number(match[1]), Number(match[2])) : null
}

// RFC 5870 `geo:<lat>,<lon>[,<alt>][;crs=…;u=…]`, plus the `?q=<lat>,<lon>` that Android maps links add (the only place the
// point is when the coordinates are `0,0`). A place name in `q` has no point, so it is `null`.
export function parseGeo(uri: string): GeoPoint | null {
  const match = /^geo:([^?]*)(?:\?(.*))?$/i.exec(uri.trim())
  if (!match) return null
  const coordinates = pair(match[1].split(';')[0])
  const q = new URLSearchParams(match[2] ?? '').get('q')
  const fromQuery = pair(q ?? '')
  const origin = coordinates !== null && coordinates.latitude === 0 && coordinates.longitude === 0
  return origin && q !== null ? fromQuery : (coordinates ?? fromQuery)
}

// The twin of the backend's `bot/location_text.py::map_url`, same shapes (checked against `__tests__/map-url-cases.json` on both sides).
export function mapUrl(geo: GeoPoint, provider: MapProvider | string): string {
  const latitude = geo.latitude.toFixed(6)
  const longitude = geo.longitude.toFixed(6)
  switch (provider) {
    case 'geo':
      return `geo:${latitude},${longitude}?q=${latitude},${longitude}`
    case 'apple':
      return `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}`
    case 'apple_app':
      return `maps://?ll=${latitude},${longitude}&q=${latitude},${longitude}`
    case 'google':
      return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
    case 'google_app':
      return `comgooglemaps://?q=${latitude},${longitude}`
    default:
      return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`
  }
}
